import React, { useState } from 'react';

// Clean, branded placeholder shown when an item has no image or the image fails
// to load — a monogram tile + wordmark, so the catalog reads as intentional
// rather than broken ("Image error").
function Placeholder({ item, height }) {
  const initial = String(item?.name || '?').trim().charAt(0).toUpperCase() || '?';
  const tile = height === 160 ? 46 : 54;
  return (
    <div style={{
      height, width: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 10,
      background: 'linear-gradient(135deg, #f7f9f8 0%, #eef2f0 60%, #e9efec 100%)',
      borderBottom: '1px solid var(--border)',
    }}>
      <div style={{
        width: tile, height: tile, borderRadius: 15, background: 'var(--green)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: tile * 0.42, fontWeight: 800, fontFamily: 'var(--font-serif)',
        boxShadow: '0 4px 12px rgba(27,92,58,0.22)',
      }}>{initial}</div>
      <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>Kanjusee</span>
    </div>
  );
}

export default function ProductCardMedia({ item, height = 180 }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const images = Array.isArray(item.images) && item.images.length > 0 ? item.images : (item.image ? [item.image] : []);
  const hasMultiple = images.length > 1;

  const handlePrev = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (e, idx) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveIndex(idx);
  };

  if (images.length === 0) {
    return <Placeholder item={item} height={height} />;
  }

  const currentImage = images[activeIndex];

  return (
    <div
      style={{
        height,
        position: 'relative',
        overflow: 'hidden',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: '1px solid var(--border)',
        padding: 12,
        userSelect: 'none'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img
        src={currentImage}
        alt={`${item.name} - view ${activeIndex + 1}`}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          display: 'block',
          transition: 'transform 0.35s ease'
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        onError={(e) => {
          // Broken/missing remote image -> reveal the branded placeholder
          e.target.style.display = 'none';
          const fallback = e.target.nextSibling;
          if (fallback) fallback.style.display = 'flex';
        }}
      />

      {/* Branded fallback (hidden until the image errors) */}
      <div style={{ display: 'none', width: '100%', height: '100%' }}>
        <Placeholder item={item} height={height} />
      </div>

      {/* Prev Arrow */}
      {hasMultiple && isHovered && (
        <button
          onClick={handlePrev}
          style={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid var(--border)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--green)',
            fontSize: 16,
            fontWeight: 'bold',
            cursor: 'pointer',
            zIndex: 10,
            padding: 0,
            lineHeight: 1,
            transition: 'background 0.15s, transform 0.1s'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
        >
          ‹
        </button>
      )}

      {/* Next Arrow */}
      {hasMultiple && isHovered && (
        <button
          onClick={handleNext}
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid var(--border)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--green)',
            fontSize: 16,
            fontWeight: 'bold',
            cursor: 'pointer',
            zIndex: 10,
            padding: 0,
            lineHeight: 1,
            transition: 'background 0.15s, transform 0.1s'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
        >
          ›
        </button>
      )}

      {/* Multiple images indicator dots */}
      {hasMultiple && (
        <div style={{
          position: 'absolute',
          bottom: 8,
          display: 'flex',
          gap: 5,
          background: 'rgba(255, 255, 255, 0.85)',
          padding: '4px 8px',
          borderRadius: 12,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          zIndex: 8
        }}>
          {images.map((_, i) => (
            <button
              key={i}
              onClick={(e) => handleDotClick(e, i)}
              style={{
                width: i === activeIndex ? 14 : 6,
                height: 6,
                borderRadius: 3,
                background: i === activeIndex ? 'var(--green)' : '#cbd5e1',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: 'none'
              }}
              title={`View image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
